import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { generateLocalizedUrl } from '@/lib/slugUtils';
import { supportedLanguages, type SupportedLanguage } from '@/lib/i18n';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import SEOHead from '@/components/SEOHead';
import Breadcrumb from '@/components/Breadcrumb';
import PageSpecificFAQStructuredData from '@/components/PageSpecificFAQStructuredData';
import { 
  Calculator, 
  Wrench,
  Users, 
  Target,
  Clock,
  TrendingUp,
  Shield,
  Sparkles,
  Share2,
  ArrowRight,
  Check,
  ChevronRight,
  Zap,
  Award,
  FileText,
  Lightbulb,
  CheckCircle2
} from 'lucide-react';

const whyIcons = [Clock, TrendingUp, Shield, Sparkles, Share2];
const seoIcons = [TrendingUp, Target, Zap, Award, Users];
const whyPriceFormsIcons = [Zap, Clock, Users, Target, Shield, Award];

export default function CraftsmenCalculator() {
  const { t, i18n } = useTranslation('craftsmen-calculator');
  const navigate = useNavigate();
  const { lang } = useParams<{ lang: SupportedLanguage }>();
  const currentLang = lang && supportedLanguages.includes(lang) ? lang : 'en';

  const handleCTAClick = () => {
    const contactUrl = generateLocalizedUrl('contact', currentLang);
    navigate(contactUrl);
  };

  return (
    <>
      <SEOHead
        title={t('meta.title')}
        description={t('meta.description')}
        lang={currentLang}
        path={generateLocalizedUrl('craftsmen-calculator', currentLang)}
      />
      
      <Header />
      
      <div className="min-h-screen bg-gradient-to-b from-background to-secondary/10">
        <div className="container mx-auto px-4 py-8 max-w-5xl">
          <Breadcrumb />

          {/* Hero Section */}
          <section className="text-center mb-16 mt-12">
            <Badge variant="secondary" className="mb-6 text-base px-6 py-2" data-testid="badge-hero">
              <Wrench className="w-4 h-4 mr-2 inline" />
              {t('hero.badge')}
            </Badge>
            
            <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent" data-testid="title-hero">
              {t('hero.title')}
            </h1>
            
            <p className="text-xl md:text-2xl text-muted-foreground mb-4 font-medium" data-testid="text-subtitle">
              {t('hero.subtitle')}
            </p>
            
            <p className="text-lg text-muted-foreground mb-4 max-w-3xl mx-auto" data-testid="text-intro">
              {t('hero.intro')}
            </p>
            
            <p className="text-lg text-muted-foreground mb-4 max-w-3xl mx-auto" data-testid="text-description">
              {t('hero.description')}
            </p>
            
            <p className="text-lg text-foreground mb-8 max-w-3xl mx-auto font-medium" data-testid="text-conclusion">
              {t('hero.conclusion')}
            </p>
            
            <Button size="lg" onClick={handleCTAClick} className="text-lg px-8 py-6" data-testid="button-cta-hero">
              {t('hero.cta')} <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </section>

          {/* What Is Section */}
          <section className="mb-16">
            <Card className="border-2" data-testid="card-what">
              <CardContent className="p-8">
                <div className="flex items-center mb-6">
                  <Calculator className="h-8 w-8 text-primary mr-3" />
                  <h2 className="text-3xl font-bold" data-testid="title-what">{t('what.title')}</h2>
                </div>
                
                <p className="text-lg text-muted-foreground mb-6" data-testid="text-what-description">
                  {t('what.description')}
                </p>
                
                <ul className="space-y-3 mb-6">
                  {(t('what.questions', { returnObjects: true }) as string[]).map((question, index) => (
                    <li key={index} className="flex items-start" data-testid={`list-item-question-${index}`}>
                      <ChevronRight className="h-5 w-5 text-primary mr-2 mt-1 flex-shrink-0" />
                      <span className="text-lg">{question}</span>
                    </li>
                  ))}
                </ul>
                
                <p className="text-lg text-foreground font-medium" data-testid="text-what-conclusion">
                  {t('what.conclusion')}
                </p>
              </CardContent>
            </Card>
          </section>

          {/* Who Uses It Table */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-center mb-8" data-testid="title-who-uses">
              {t('whoUses.title')}
            </h2>
            
            <div className="overflow-x-auto">
              <table className="w-full border-collapse bg-card rounded-lg overflow-hidden" data-testid="table-who-uses">
                <thead className="bg-muted">
                  <tr>
                    <th className="p-4 text-left font-semibold" data-testid="header-industry">{t('whoUses.table.headers.industry')}</th>
                    <th className="p-4 text-left font-semibold" data-testid="header-calculator">{t('whoUses.table.headers.calculator')}</th>
                    <th className="p-4 text-left font-semibold" data-testid="header-purpose">{t('whoUses.table.headers.purpose')}</th>
                  </tr>
                </thead>
                <tbody>
                  {(t('whoUses.table.rows', { returnObjects: true }) as Array<{ industry: string; calculator: string; purpose: string }>).map((row, index) => (
                    <tr key={index} className="border-t border-border" data-testid={`row-craftsmen-${index}`}>
                      <td className="p-4 font-medium" data-testid={`cell-industry-${index}`}>{row.industry}</td>
                      <td className="p-4 text-muted-foreground" data-testid={`cell-calculator-${index}`}>{row.calculator}</td>
                      <td className="p-4 text-muted-foreground" data-testid={`cell-purpose-${index}`}>{row.purpose}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* How It Works */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-center mb-12" data-testid="title-how-it-works">
              {t('howItWorks.title')}
            </h2>
            
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              {(t('howItWorks.steps', { returnObjects: true }) as Array<{ number: string; title: string; description: string }>).map((step, index) => (
                <Card key={index} className="relative" data-testid={`card-step-${index}`}>
                  <CardContent className="p-6">
                    <div className="absolute -top-4 -left-4 bg-primary text-primary-foreground rounded-full w-12 h-12 flex items-center justify-center font-bold text-xl" data-testid={`badge-step-${step.number}`}>
                      {step.number}
                    </div>
                    <h3 className="text-xl font-semibold mb-3 mt-2" data-testid={`title-step-${index}`}>{step.title}</h3>
                    <p className="text-muted-foreground" data-testid={`text-step-${index}`}>{step.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
            
            <Card className="bg-primary/5 border-primary/20" data-testid="card-benefit">
              <CardContent className="p-6">
                <div className="flex items-start">
                  <Lightbulb className="h-6 w-6 text-primary mr-3 mt-1 flex-shrink-0" />
                  <p className="text-lg font-medium" data-testid="text-benefit">
                    {t('howItWorks.benefit')}
                  </p>
                </div>
              </CardContent>
            </Card>
          </section>

          {/* Why Use It */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-center mb-12" data-testid="title-why">
              {t('why.title')}
            </h2>
            
            <div className="space-y-6">
              {(t('why.items', { returnObjects: true }) as Array<{ number: string; title: string; description: string }>).map((item, index) => {
                const Icon = whyIcons[index] || Check;
                return (
                  <Card key={index} className="border-l-4 border-l-primary" data-testid={`card-why-${index}`}>
                    <CardContent className="p-6">
                      <div className="flex items-start">
                        <div className="mr-4 mt-1">
                          <Icon className="h-8 w-8 text-primary" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-xl font-semibold mb-3" data-testid={`title-why-${index}`}>
                            {item.number}. {item.title}
                          </h3>
                          <p className="text-muted-foreground" data-testid={`text-why-${index}`}>{item.description}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </section>

          {/* Case Study */}
          <section className="mb-16">
            <Card className="bg-gradient-to-br from-primary/10 to-secondary/10 border-2" data-testid="card-case">
              <CardContent className="p-8">
                <div className="flex items-center mb-6">
                  <Award className="h-8 w-8 text-primary mr-3" />
                  <h2 className="text-3xl font-bold" data-testid="title-case">{t('case.title')}</h2>
                </div>
                
                <p className="text-lg mb-4" data-testid="text-case-description">
                  {t('case.description')}
                </p>
                
                <p className="text-lg font-medium mb-4" data-testid="text-case-after">
                  {t('case.afterPeriod')}
                </p>
                
                <div className="grid md:grid-cols-3 gap-4 mb-6">
                  {(t('case.results', { returnObjects: true }) as string[]).map((result, index) => (
                    <div key={index} className="flex items-start bg-background/50 rounded-lg p-4" data-testid={`result-${index}`}>
                      <CheckCircle2 className="h-6 w-6 text-primary mr-2 flex-shrink-0 mt-0.5" />
                      <span className="font-medium">{result}</span>
                    </div>
                  ))}
                </div>
                
                <blockquote className="border-l-4 border-primary pl-6 italic text-lg mb-2" data-testid="quote-case">
                  "{t('case.quote')}"
                </blockquote>
                <p className="text-muted-foreground pl-6" data-testid="author-case">
                  — {t('case.author')}
                </p>
              </CardContent>
            </Card>
          </section>

          {/* Pricing Table */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-center mb-12" data-testid="title-pricing">
              {t('pricing.title')}
            </h2>
            
            <div className="overflow-x-auto mb-6">
              <table className="w-full border-collapse bg-card rounded-lg overflow-hidden" data-testid="table-pricing">
                <thead className="bg-muted">
                  <tr>
                    <th className="p-4 text-left font-semibold" data-testid="header-solution">{t('pricing.table.headers.solution')}</th>
                    <th className="p-4 text-left font-semibold" data-testid="header-price">{t('pricing.table.headers.price')}</th>
                    <th className="p-4 text-left font-semibold" data-testid="header-features">{t('pricing.table.headers.features')}</th>
                  </tr>
                </thead>
                <tbody>
                  {(t('pricing.table.rows', { returnObjects: true }) as Array<{ solution: string; price: string; features: string }>).map((row, index) => (
                    <tr key={index} className="border-t border-border" data-testid={`row-pricing-${index}`}>
                      <td className="p-4 font-medium" data-testid={`cell-solution-${index}`}>{row.solution}</td>
                      <td className="p-4 text-primary font-semibold" data-testid={`cell-price-${index}`}>{row.price}</td>
                      <td className="p-4 text-muted-foreground" data-testid={`cell-features-${index}`}>{row.features}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            <p className="text-center text-muted-foreground" data-testid="text-pricing-note">
              {t('pricing.note')}
            </p>
          </section>

          {/* SEO Benefits */}
          <section className="mb-16">
            <Card className="border-2" data-testid="card-seo">
              <CardContent className="p-8">
                <h2 className="text-3xl font-bold mb-6" data-testid="title-seo">{t('seo.title')}</h2>
                
                <p className="text-lg text-muted-foreground mb-4" data-testid="text-seo-description">
                  {t('seo.description')}
                </p>
                
                <p className="text-lg font-medium mb-6" data-testid="text-seo-intro">
                  {t('seo.intro')}
                </p>
                
                <div className="grid md:grid-cols-2 gap-4 mb-6">
                  {(t('seo.keywords', { returnObjects: true }) as string[]).map((keyword, index) => {
                    const Icon = seoIcons[index] || Target;
                    return (
                      <div key={index} className="flex items-center bg-secondary/30 rounded-lg p-4" data-testid={`keyword-${index}`}>
                        <Icon className="h-5 w-5 text-primary mr-3 flex-shrink-0" />
                        <span className="font-medium">{keyword}</span>
                      </div>
                    );
                  })}
                </div>
                
                <p className="text-lg text-foreground font-medium" data-testid="text-seo-conclusion">
                  {t('seo.conclusion')}
                </p>
              </CardContent>
            </Card>
          </section>

          {/* Tips Section */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-center mb-12" data-testid="title-tips">
              {t('tips.title')}
            </h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              {(t('tips.items', { returnObjects: true }) as Array<{ title: string; description: string }>).map((tip, index) => (
                <Card key={index} data-testid={`card-tip-${index}`}>
                  <CardContent className="p-6">
                    <div className="flex items-start">
                      <div className="bg-primary/10 rounded-full p-2 mr-4">
                        <Check className="h-6 w-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold mb-2" data-testid={`title-tip-${index}`}>{tip.title}</h3>
                        <p className="text-muted-foreground" data-testid={`text-tip-${index}`}>{tip.description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>

          {/* Why PriceForms */}
          <section className="mb-16">
            <Card className="bg-secondary/20 border-2" data-testid="card-why-priceforms">
              <CardContent className="p-8">
                <h2 className="text-3xl font-bold text-center mb-8" data-testid="title-why-priceforms">
                  {t('whyPriceForms.title')}
                </h2>
                
                <div className="grid md:grid-cols-2 gap-4">
                  {(t('whyPriceForms.features', { returnObjects: true }) as string[]).map((feature, index) => {
                    const Icon = whyPriceFormsIcons[index] || Check;
                    return (
                      <div key={index} className="flex items-start" data-testid={`feature-${index}`}>
                        <Icon className="h-6 w-6 text-primary mr-3 mt-1 flex-shrink-0" />
                        <span className="text-lg">{feature}</span>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </section>

          {/* FAQ Section */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-center mb-12" data-testid="title-faq">
              {t('faq.title')}
            </h2>
            
            <div className="space-y-6">
              {(t('faq.items', { returnObjects: true }) as Array<{ question: string; answer: string }>).map((item, index) => (
                <Card key={index} data-testid={`card-faq-${index}`}>
                  <CardContent className="p-6">
                    <h3 className="text-xl font-semibold mb-3 flex items-start" data-testid={`question-${index}`}>
                      <span className="text-primary mr-2">Q:</span>
                      {item.question}
                    </h3>
                    <p className="text-muted-foreground ml-7" data-testid={`answer-${index}`}>
                      <span className="font-semibold text-foreground">A:</span> {item.answer}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>

          {/* Final CTA */}
          <section className="mb-16">
            <Card className="bg-gradient-to-br from-background via-secondary/20 to-background border-2" data-testid="card-final-cta">
              <CardContent className="p-12 text-center">
                <h2 className="text-3xl md:text-4xl font-bold mb-6" data-testid="title-final-cta">
                  {t('finalCta.title')}
                </h2>
                
                <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto" data-testid="text-final-cta">
                  {t('finalCta.description')}
                </p>
                
                <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-6">
                  <Button size="lg" onClick={handleCTAClick} className="text-lg px-8 py-6" data-testid="button-cta-primary">
                    {t('finalCta.cta')} <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                  
                  <span className="text-muted-foreground" data-testid="text-or">{t('finalCta.or')}</span>
                  
                  <Button size="lg" variant="outline" onClick={handleCTAClick} className="text-lg px-8 py-6" data-testid="button-cta-secondary">
                    {t('finalCta.secondaryCta')}
                  </Button>
                </div>
                
                <p className="text-lg text-muted-foreground" data-testid="text-guarantee">
                  {t('finalCta.guarantee')}
                </p>
              </CardContent>
            </Card>
          </section>
        </div>
      </div>
      
      <PageSpecificFAQStructuredData namespace="craftsmen-calculator" faqKey="faq.items" />
      <Footer />
    </>
  );
}
